import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  LinearProgress,
  Alert,
  AlertTitle,
  Link,
  Tooltip,
  IconButton,
  Snackbar,
  CircularProgress,
  Divider,
  Drawer
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import ChatIcon from '@mui/icons-material/Chat';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from 'axios';


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const FlowDetailsModal = ({ open, onClose, messageId, getAuthToken }) => {
  const [flowData, setFlowData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [selectedChunk, setSelectedChunk] = useState(null);
  const [chunkContent, setChunkContent] = useState(null);
  const [loadingChunk, setLoadingChunk] = useState(false);
  // File viewer state
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [loadingFile, setLoadingFile] = useState(false);

  useEffect(() => {
    if (open && messageId) {
      fetchFlowDetails();
    }
  }, [open, messageId]);

  const handleChunkClick = async (chunk) => {
    setSelectedChunk(chunk);
    setLoadingChunk(true);
    setChunkContent(null);
    
    try {
      const token = await getAuthToken();
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/chunk/${chunk.chunk_id}/content`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      setChunkContent(response.data);
    } catch (err) {
      console.error('Error fetching chunk content:', err);
      setChunkContent({ error: 'Failed to load chunk content' });
    } finally {
      setLoadingChunk(false);
    }
  };

  const handleCloseDrawer = () => {
    setSelectedChunk(null);
    setChunkContent(null);
  };

  const handleFileClick = async (file) => {
    setSelectedFile(file);
    setLoadingFile(true);
    setFileContent(null);
    
    try {
      // Check if file_hash exists
      if (!file.file_hash) {
        console.error('File object missing file_hash:', file);
        setFileContent({ error: `File hash not available for ${file.filename}` });
        setLoadingFile(false);
        return;
      }

      const token = await getAuthToken();
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/file/${file.file_hash}/content`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      setFileContent(response.data);
    } catch (err) {
      console.error('Error fetching file content:', err);
      console.error('File object was:', file);
      setFileContent({ error: `Failed to load file content: ${err.response?.data?.detail || err.message}` });
    } finally {
      setLoadingFile(false);
    }
  };

  const handleCloseFileDrawer = () => {
    setSelectedFile(null);
    setFileContent(null);
  };

  const fetchFlowDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = await getAuthToken();
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/message/${messageId}/flow-details`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setFlowData(response.data);
    } catch (err) {
      console.error('Error fetching flow details:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to fetch flow details');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, label = 'Content') => {
    navigator.clipboard.writeText(text);
    setSnackbarMessage(`${label} copied to clipboard!`);
    setSnackbarOpen(true);
  };

  const handleCopyAll = () => {
    const textContent = JSON.stringify(flowData, null, 2);
    handleCopy(textContent, 'All details');
  };

  const handleDownloadJSON = () => {
    const dataStr = JSON.stringify(flowData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `flow-details-${messageId}.json`;
    link.click();
    URL.revokeObjectURL(url);
    setSnackbarMessage('JSON downloaded!');
    setSnackbarOpen(true);
  };

  const formatDuration = (ms) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const renderUserInfo = () => {
    if (!flowData?.query_info) return null;
    const { user_source, chatwoot_contact, platform_user } = flowData.query_info;

    if (user_source === 'chatwoot' && chatwoot_contact) {
      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            👤 User Details
          </Typography>
          <Box sx={{ pl: 2 }}>
            <Chip
              icon={<ChatIcon />}
              label={`Chatwoot Conversation #${chatwoot_contact.conversation_id}`}
              size="small"
              color="primary"
              sx={{ mb: 1 }}
            />
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Contact:</strong> {chatwoot_contact.contact_name || 'Anonymous'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Contact ID:</strong> {chatwoot_contact.contact_id}
                </Typography>
              </Grid>
              {chatwoot_contact.contact_email && (
                <Grid item xs={12}>
                  <Typography variant="body2">
                    <strong>Email:</strong> {chatwoot_contact.contact_email}
                  </Typography>
                </Grid>
              )}
              {chatwoot_contact.contact_phone && (
                <Grid item xs={12}>
                  <Typography variant="body2">
                    <strong>Phone:</strong> {chatwoot_contact.contact_phone}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Channel:</strong> {chatwoot_contact.channel?.replace('Channel::', '') || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Status:</strong> {chatwoot_contact.status || 'N/A'}
                </Typography>
              </Grid>
              {chatwoot_contact.browser && (
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                    <strong>Browser:</strong> {chatwoot_contact.browser.browser_name} {chatwoot_contact.browser.browser_version}
                    {' on '}{chatwoot_contact.browser.platform_name} {chatwoot_contact.browser.platform_version}
                  </Typography>
                </Grid>
              )}
              {chatwoot_contact.referer && (
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>
                    <strong>From:</strong>{' '}
                    <Link href={chatwoot_contact.referer} target="_blank" rel="noopener noreferrer">
                      {chatwoot_contact.referer}
                    </Link>
                  </Typography>
                </Grid>
              )}
              {chatwoot_contact.last_seen_at && (
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                    <strong>Last Seen:</strong> {formatTimestamp(chatwoot_contact.last_seen_at)}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </Box>
      );
    } else if (user_source === 'platform' && platform_user) {
      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            👤 User Details
          </Typography>
          <Box sx={{ pl: 2 }}>
            <Chip
              icon={<PersonIcon />}
              label="Direct Platform Access"
              size="small"
              color="secondary"
              sx={{ mb: 1 }}
            />
            <Typography variant="body2">
              <strong>User ID:</strong> {platform_user.user_id}
            </Typography>
            {platform_user.email && (
              <Typography variant="body2">
                <strong>Email:</strong> {platform_user.email}
              </Typography>
            )}
          </Box>
        </Box>
      );
    }
    return null;
  };

  const renderQueryInfo = () => {
    if (!flowData?.query_info) return null;
    const { question, timestamp, response_timestamp, model_used, session } = flowData.query_info;

    return (
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">📝 Query Information</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <Typography variant="body1" sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <strong>User Question:</strong> "{question}"
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Asked At:</strong> {formatTimestamp(timestamp)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Answered At:</strong> {formatTimestamp(response_timestamp)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Model Used:</strong> {model_used || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Company:</strong> {session?.company_name || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <strong>Session ID:</strong> 
                  <code style={{ fontSize: '0.85rem' }}>{session?.session_id}</code>
                  <IconButton size="small" onClick={() => handleCopy(session?.session_id, 'Session ID')}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Typography>
              </Grid>
            </Grid>
            {renderUserInfo()}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderFAQSearch = () => {
    if (!flowData?.faq_search) return null;
    const { enabled, faqs_found, high_confidence, action, top_score } = flowData.faq_search;

    const getStatusColor = () => {
      if (high_confidence) return 'success';
      if (faqs_found > 0) return 'warning';
      return 'default';
    };

    const getStatusText = () => {
      if (high_confidence) return 'High-confidence FAQ match (≥0.75)';
      if (faqs_found > 0) return `${faqs_found} FAQ(s) found (below 0.75 threshold)`;
      return 'No FAQs found';
    };

    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">🔍 FAQ Search (Stage 0)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <Alert severity={high_confidence ? 'success' : faqs_found > 0 ? 'info' : 'warning'} sx={{ mb: 2 }}>
              <AlertTitle>{getStatusText()}</AlertTitle>
              {high_confidence ? (
                <Typography variant="body2">
                  ✅ FAQ answer returned directly. Document retrieval was skipped for faster response.
                </Typography>
              ) : faqs_found > 0 ? (
                <Typography variant="body2">
                  FAQ score: {top_score?.toFixed(3)} - Below threshold, continued to document retrieval.
                </Typography>
              ) : (
                <Typography variant="body2">
                  No matching FAQs in the company knowledge base. Proceeded to document search.
                </Typography>
              )}
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Enabled:</strong> {enabled ? '✅ Yes' : '❌ No'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>FAQs Found:</strong> {faqs_found}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>High Confidence:</strong> {high_confidence ? '✅ Yes (≥0.75)' : '❌ No'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Action Taken:</strong> {action === 'returned_faq_directly' ? 'Returned FAQ' : 'Continued to retrieval'}
                </Typography>
              </Grid>
            </Grid>
            
            {flowData.faq_search.faq_results && flowData.faq_search.faq_results.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>FAQ Matches:</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Rank</strong></TableCell>
                        <TableCell><strong>FAQ</strong></TableCell>
                        <TableCell><strong>Score</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {flowData.faq_search.faq_results.map((faq) => (
                        <TableRow key={faq.rank}>
                          <TableCell>{faq.rank}</TableCell>
                          <TableCell>{faq.filename}</TableCell>
                          <TableCell>
                            <Chip 
                              label={faq.score.toFixed(3)} 
                              size="small" 
                              color={faq.score >= 0.75 ? 'success' : faq.score >= 0.60 ? 'warning' : 'default'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderDocumentRetrieval = () => {
    if (!flowData?.document_retrieval) return null;
    const { rag_mode, chunks_retrieved, unique_documents, reranker_enabled } = flowData.document_retrieval;

    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">📚 Document Retrieval</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>RAG Mode:</strong> {rag_mode || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Reranker:</strong> {reranker_enabled ? '✅ Enabled' : '❌ Disabled'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Chunks Retrieved:</strong> {chunks_retrieved}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Unique Documents:</strong> {unique_documents}
                </Typography>
              </Grid>
            </Grid>
            {rag_mode === 'two_stage_summary' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Two-Stage Retrieval:</strong> Stage 1 identified {unique_documents} relevant documents. 
                  Stage 2 refined to {chunks_retrieved} specific chunks from top documents.
                </Typography>
              </Alert>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderFilesUsed = () => {
    if (!flowData?.files_used || flowData.files_used.length === 0) return null;

    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">📂 Files Used (Stage 1 - Document Summaries)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Rank</strong></TableCell>
                  <TableCell><strong>Filename</strong></TableCell>
                  <TableCell><strong>Source URL</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {flowData.files_used.map((file) => (
                  <TableRow key={file.rank}>
                    <TableCell>{file.rank}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {file.filename}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {file.source_url ? (
                        <Link href={file.source_url} target="_blank" rel="noopener" sx={{ fontSize: '0.875rem' }}>
                          {file.source_url}
                        </Link>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View file content">
                        <IconButton 
                          size="small" 
                          onClick={() => handleFileClick(file)}
                          color="primary"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderRetrievedChunks = () => {
    if (!flowData?.retrieved_chunks || flowData.retrieved_chunks.length === 0) return null;

    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">📄 Retrieved Chunks ({flowData.retrieved_chunks.length})</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Rank</strong></TableCell>
                  <TableCell><strong>Filename</strong></TableCell>
                  <TableCell><strong>Score</strong></TableCell>
                  <TableCell><strong>Source</strong></TableCell>
                  <TableCell><strong>Page</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {flowData.retrieved_chunks.map((chunk) => (
                  <TableRow 
                    key={chunk.rank} 
                    hover={chunk.has_chunk_content}
                    onClick={chunk.has_chunk_content ? () => handleChunkClick(chunk) : undefined}
                    sx={{ 
                      cursor: chunk.has_chunk_content ? 'pointer' : 'default', 
                      '&:hover': chunk.has_chunk_content ? { backgroundColor: 'action.hover' } : {}
                    }}
                  >
                    <TableCell>{chunk.rank}</TableCell>
                    <TableCell>
                      <Tooltip title={chunk.has_chunk_content ? `${chunk.filename} (Click to view)` : `${chunk.filename} (File-level provenance)`} arrow>
                        <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {chunk.filename}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={chunk.score.toFixed(3)} 
                        size="small" 
                        color={chunk.score >= 0.75 ? 'success' : chunk.score >= 0.60 ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={chunk.is_faq ? 'FAQ' : 'Document'} 
                        size="small" 
                        color={chunk.is_faq ? 'secondary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{chunk.page_number || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderGroundingCheck = () => {
    if (!flowData?.grounding_check) return null;
    const { enabled, threshold, score, passed, escalated, escalation_details } = flowData.grounding_check;

    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">✓ Grounding Check</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            {!enabled ? (
              <Alert severity="info">
                <AlertTitle>Not Enabled</AlertTitle>
                <Typography variant="body2">
                  Grounding check is not enabled for this session.
                </Typography>
              </Alert>
            ) : escalated ? (
              <Alert severity="warning">
                <AlertTitle>Grounding Check Failed - Escalated</AlertTitle>
                <Typography variant="body2">
                  Score: {score?.toFixed(3)} (threshold: {threshold}) - Query was escalated to a better model.
                </Typography>
              </Alert>
            ) : (
              <Alert severity="success">
                <AlertTitle>Grounding Check Passed</AlertTitle>
                <Typography variant="body2">
                  Response is well-grounded in the retrieved documents.
                </Typography>
              </Alert>
            )}
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Enabled:</strong> {enabled ? '✅ Yes' : '❌ No'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Threshold:</strong> {threshold}
                </Typography>
              </Grid>
              {enabled && (
                <>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Score:</strong> {score !== null ? score.toFixed(3) : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Result:</strong> {escalated ? '❌ Failed (Escalated)' : '✅ Passed'}
                    </Typography>
                  </Grid>
                </>
              )}
              {escalation_details && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Original Model:</strong> {escalation_details.original_model}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Escalated To:</strong> {escalation_details.escalated_model}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderLLMGeneration = () => {
    if (!flowData?.llm_generation) return null;
    const { model, prompt_tokens, completion_tokens, total_tokens, cost_usd, duration_ms, user_question, assistant_answer } = flowData.llm_generation;

    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">🤖 LLM Generation</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            {user_question && (
              <Paper elevation={1} sx={{ p: 2, mb: 2, backgroundColor: 'blue.50', borderLeft: 3, borderColor: 'primary.main' }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  <strong>User Question:</strong>
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {user_question}
                </Typography>
              </Paper>
            )}
            
            {assistant_answer && (
              <Paper elevation={1} sx={{ p: 2, mb: 2, backgroundColor: 'green.50', borderLeft: 3, borderColor: 'success.main' }}>
                <Typography variant="subtitle2" color="success.main" gutterBottom>
                  <strong>Assistant Answer:</strong>
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {assistant_answer}
                </Typography>
              </Paper>
            )}
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2">
                  <strong>Model:</strong> {model || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Input Tokens:</strong> {prompt_tokens !== null ? prompt_tokens.toLocaleString() : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Output Tokens:</strong> {completion_tokens !== null ? completion_tokens.toLocaleString() : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Total Tokens:</strong> {total_tokens !== null ? total_tokens.toLocaleString() : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Cost:</strong> {cost_usd !== null ? `$${cost_usd.toFixed(4)}` : 'Not configured'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Duration:</strong> {formatDuration(duration_ms)}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderPerformance = () => {
    if (!flowData?.performance) return null;
    const { total_ms, breakdown, trace_id } = flowData.performance;

    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">⚡ Performance</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Total Duration:</strong> {formatDuration(total_ms)}
            </Typography>
            {trace_id && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Langfuse Trace ID:</strong> {trace_id}
              </Typography>
            )}
            {breakdown && Object.keys(breakdown).length > 0 ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Stage Breakdown:</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Stage</strong></TableCell>
                        <TableCell><strong>Duration</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(breakdown).map(([stage, duration_ms]) => (
                        <TableRow key={stage}>
                          <TableCell>{stage}</TableCell>
                          <TableCell>{formatDuration(duration_ms)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : !total_ms && (
              <Alert severity="info">
                <Typography variant="body2">
                  Detailed performance breakdown not available. This requires Langfuse instrumentation.
                </Typography>
              </Alert>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5">Flow Analysis Details</Typography>
            <Box>
              <Tooltip title="Copy all details">
                <IconButton onClick={handleCopyAll} size="small">
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download as JSON">
                <IconButton onClick={handleDownloadJSON} size="small">
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">
              <AlertTitle>Error Loading Flow Details</AlertTitle>
              {error}
            </Alert>
          ) : flowData ? (
            <Box>
              {renderQueryInfo()}
              {renderFAQSearch()}
              {renderDocumentRetrieval()}
              {renderFilesUsed()}
              {renderRetrievedChunks()}
              {renderGroundingCheck()}
              {renderLLMGeneration()}
              {renderPerformance()}
            </Box>
          ) : (
            <Typography>No data available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />

      <Drawer
        anchor="right"
        open={selectedChunk !== null}
        onClose={handleCloseDrawer}
        sx={{ zIndex: 1400 }}
      >
        <Box sx={{ width: 600, p: 3 }}>
          {selectedChunk && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Chunk Content</Typography>
                <Button onClick={handleCloseDrawer}>Close</Button>
              </Box>
              
              <Paper elevation={2} sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Filename:</strong> {selectedChunk.filename}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Score:</strong> {selectedChunk.score.toFixed(3)}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Rank:</strong> {selectedChunk.rank}
                </Typography>
                {selectedChunk.page_number && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Page:</strong> {selectedChunk.page_number}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  <strong>Source:</strong> {selectedChunk.is_faq ? 'FAQ' : 'Document'}
                </Typography>
              </Paper>

              <Divider sx={{ my: 2 }} />

              {loadingChunk ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress />
                </Box>
              ) : chunkContent?.error ? (
                <Alert severity="error">{chunkContent.error}</Alert>
              ) : chunkContent ? (
                <Paper elevation={1} sx={{ p: 2, backgroundColor: 'background.paper' }}>
                  <Typography variant="subtitle2" gutterBottom>Content:</Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      whiteSpace: 'pre-wrap', 
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      lineHeight: 1.6
                    }}
                  >
                    {chunkContent.content || 'No content available'}
                  </Typography>
                  
                  {chunkContent.metadata && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                      <Typography variant="subtitle2" gutterBottom>Metadata:</Typography>
                      <Typography variant="body2" component="pre" sx={{ fontSize: '0.75rem' }}>
                        {JSON.stringify(chunkContent.metadata, null, 2)}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              ) : null}
            </>
          )}
        </Box>
      </Drawer>

      {/* File Content Drawer (Eye Icon Feature) */}
      <Drawer
        anchor="right"
        open={selectedFile !== null}
        onClose={handleCloseFileDrawer}
        sx={{ zIndex: 1400 }}
      >
        <Box sx={{ width: 700, p: 3 }}>
          {selectedFile && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">File Content</Typography>
                <Button onClick={handleCloseFileDrawer}>Close</Button>
              </Box>
              
              <Paper elevation={2} sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Filename:</strong> {selectedFile.filename}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Rank:</strong> {selectedFile.rank}
                </Typography>
                {selectedFile.source_url && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>URL:</strong>{' '}
                    <Link href={selectedFile.source_url} target="_blank" rel="noopener">
                      {selectedFile.source_url}
                    </Link>
                  </Typography>
                )}
              </Paper>

              <Divider sx={{ my: 2 }} />

              {loadingFile ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress />
                </Box>
              ) : fileContent?.error ? (
                <Alert severity="error">{fileContent.error}</Alert>
              ) : fileContent ? (
                <>
                  {/* Searchable Summary (Blue) */}
                  <Paper elevation={1} sx={{ p: 2, mb: 2, backgroundColor: '#e3f2fd' }}>
                    <Typography variant="subtitle2" gutterBottom color="primary">
                      Searchable Summary (for RAG):
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        whiteSpace: 'pre-wrap',
                        fontSize: '0.875rem',
                        lineHeight: 1.6,
                        maxHeight: 200,
                        overflowY: 'auto'
                      }}
                    >
                      {fileContent.searchable_summary}
                    </Typography>
                  </Paper>

                  {/* General Summary (Orange) */}
                  <Paper elevation={1} sx={{ p: 2, mb: 2, backgroundColor: '#fff3e0' }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ color: '#e65100' }}>
                      Summary (general):
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        whiteSpace: 'pre-wrap',
                        fontSize: '0.875rem',
                        lineHeight: 1.6,
                        maxHeight: 200,
                        overflowY: 'auto'
                      }}
                    >
                      {fileContent.doc_summary}
                    </Typography>
                  </Paper>

                  {/* Full Content (Gray) */}
                  <Paper elevation={1} sx={{ p: 2, backgroundColor: 'grey.50' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Full File Content:
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        lineHeight: 1.6,
                        maxHeight: 400,
                        overflowY: 'auto'
                      }}
                    >
                      {fileContent.extracted_full_text}
                    </Typography>
                  </Paper>

                  {/* Metadata */}
                  {fileContent.metadata && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                      <Typography variant="subtitle2" gutterBottom>Metadata:</Typography>
                      <Typography variant="body2" component="pre" sx={{ fontSize: '0.75rem' }}>
                        {JSON.stringify(fileContent.metadata, null, 2)}
                      </Typography>
                    </Box>
                  )}
                </>
              ) : null}
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default FlowDetailsModal;

